import { Grid } from "@mui/material";
import {
  Button,
  InputItem,
  LoadingSpinner,
  Pagination,
  ToastContainer,
} from "pepsico-ds";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmptyImage from "../../assets/widget/create_new_widget_empty.svg";
import BonusGridCard from "../../components/campaign/bonusCampaign/BonusGridCard";
import BonusListView from "../../components/campaign/bonusCampaign/BonusListView";
import { getCampaignActionButtons } from "../../components/campaign/CampaignListing/CampaignActionButtons";
import CreateCampaignModal from "../../components/campaign/CreateCampaignModal";
import ButtonGroupDynamic from "../../components/common/ButtonGroupDynamic";
import JsonFormsWrapper from "../../components/jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { useAppConfig } from "../../context/AppConfigContext";
import { CampaignSetupContext } from "../../context/CampaignSetupContext";
import { GlobalConfigContext } from "../../context/GlobalConfigContext.js";
import { useBonusListPage } from "../../hooks/useBonusListPage";
import { useCampaignStateHandlerHook } from "../../hooks/useCampaignStateHandlerHook";
import { useHasAccessPermission } from "../../hooks/useHasAccessPermission";
import { FeatureToggle, useFeatureToggle } from "../../utils/featureToggle";
import "../campaigns/campaign.scss";
import { createBonusNavigation, handleSummaryNavigation } from "./utils";

const BonusListPage = () => {
  const navigate = useNavigate();
  const { campaignsConfiguration, programTimezone } =
    useContext(GlobalConfigContext);
  const {
    resetCampaignState,
    resetCurrentCampaignTypeConfig,
    setCurrentCampaignTypeConfig,
    toastData,
    setToastData,
  } = useContext(CampaignSetupContext);

  const [viewType, setViewType] = useState("grid");
  const [searchInput, setSearchInput] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const { setCampaignPresets } = useCampaignStateHandlerHook();

  const isBonusFlowEnabled = useFeatureToggle(FeatureToggle.isBonusFlowEnabled);

  // Use the enhanced useBonusListPage hook
  const {
    loading,
    campaigns,
    filteredCampaigns,
    filterBonusUiSchema,
    filterKAZJsonSchema,
    currentPage,
    handlePageChange,
    numPages,
    localFilters,
    updateLocalFilters,
    setLocalFilters,
  } = useBonusListPage();

  const hasAccess = useHasAccessPermission();
  const hasCreateCampaignAccess = hasAccess("permissions.campaign");
  const { programName } = useAppConfig();
  useEffect(() => {
    resetCurrentCampaignTypeConfig();
    resetCampaignState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchInput(value);
    updateLocalFilters(value, "title");
  };

  const handleSummaryClick = (campaign) => {
    handleSummaryNavigation(
      campaign,
      campaignsConfiguration,
      setCurrentCampaignTypeConfig,
      navigate
    );
  };

  const createBonus = () => {
    createBonusNavigation(
      campaignsConfiguration,
      setCurrentCampaignTypeConfig,
      setCampaignPresets,
      navigate
    );
  };

  const renderCampaigns = () => {
    if (loading) {
      return (
        <div className="loading-container" data-testid="utils-loading-spinner">
          <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
        </div>
      );
    }

    if (!loading && campaigns?.length === 0) {
      return (
        <div className="empty-state" data-testid="empty-state">
          <img src={EmptyImage} alt="No campaigns created" />
          <p>No campaigns created yet</p>
        </div>
      );
    }

    return viewType === "grid" ? (
      <Grid container spacing={2}>
        {campaigns?.map(
          (campaign) =>
            campaign && (
              <Grid key={campaign.id} item xs={6} md={4} lg={3}>
                <BonusGridCard
                  campaign={campaign}
                  programTimezone={programTimezone}
                  actionButtons={getCampaignActionButtons?.({
                    status: campaign?.status,
                    campaign: campaign,
                    handleSummaryClick,
                    hasAccess: hasCreateCampaignAccess,
                    isBonusFlowEnabled,
                  })}
                />
              </Grid>
            )
        )}
      </Grid>
    ) : (
      <BonusListView
        campaignData={campaigns}
        programSlug={programName}
        handleSummaryClick={handleSummaryClick}
        hasAccess={hasCreateCampaignAccess}
      />
    );
  };

  return (
    <div
      className="display-flex flex-direction-column campaign-list"
      style={{
        padding: "1rem",
        gap: "1rem",
        width: "100%",
        minHeight: "100%",
        borderRadius: "10px",
      }}
    >
      <div
        style={{
          background: "rgba(255, 255, 255, 1)",
          gap: "16px",
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          height: "calc(100% - 11px)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h2>Bonus List</h2>
          {hasCreateCampaignAccess && (
            <Button onClick={createBonus}>Create Bonus</Button>
          )}
          {modalOpen && (
            <div>
              <CreateCampaignModal
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
              />
            </div>
          )}
        </div>
        <div className="filter-container container">
          <div className="filter-container-search">
            <InputItem
              placeholder="Campaign Name"
              trailingIcon="search"
              size="medium"
              className="input-search"
              onChange={handleSearchChange}
              value={searchInput}
            />
          </div>
          <div className="filter-container-popover">
            <JsonFormsWrapper
              jsonschema={filterKAZJsonSchema}
              uischema={filterBonusUiSchema}
              data={localFilters}
              onChange={setLocalFilters}
            />
          </div>
          <ButtonGroupDynamic
            items={[
              {
                icon: "view_module",
                onClick: () => setViewType("grid"),
              },
              {
                icon: "view_list",
                onClick: () => setViewType("list"),
              },
            ]}
            size="medium"
            variant="toggle"
            selectedIndex={viewType === "grid" ? 0 : 1}
          />
        </div>
        {renderCampaigns()}
        {filteredCampaigns && filteredCampaigns?.length > 0 && (
          <Pagination
            currentPage={currentPage}
            onUpdate={handlePageChange}
            pageCount={numPages}
            size="medium"
            variant="number"
          />
        )}
      </div>
      {toastData.length > 0 && (
        <ToastContainer
          data={toastData}
          removeToast={() => setToastData([])}
          showActionIcon
        />
      )}
    </div>
  );
};

export default BonusListPage;
